import {AsyncPage, Empty} from 'Core/models/Async';
import {DateRange, EmptyDateRange} from 'Core/models/DateRange';
import {Page} from 'Core/models/Page';
import {createAction} from 'Shared/utilities/actionCreator';
import {FilterOptionUnion, FilterOptionValue} from '../../../Core/models/FilterOption';
import {PolicyApplicationDto} from '../api/api';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import * as O from 'fp-ts/lib/Option';

export enum PolicyApplicationsActionType {
  FETCH_APPLICATIONS = 'policy/FETCH_APPLICATIONS',
  CHANGE_APPLICATIONS_FILTER = 'Policy/CHANGE_APPLICATIONS_FILTER',
  CHANGE_APPLICATIONS = 'policy/CHANGE_APPLICATIONS',
  CHANGE_SORT = 'policy/CHANGE_SORT',
  CLEAR_SORT = 'policy/CLEAR_SORT',
}

export type PolicyApplicationsFilter = {
  agentNumbers: string[];
  employerName: string;
  accountNumber: string;
  writingAgent: string;
  writingAgentNumber: string;
  insuredName: string;
  insuredIdentifier: string;
  employeeName: string;
  employeeIdentifier: string;
  relation: string;
  planName: string;
  premiumMode: string;
  deductionAmount: string;
  annualDeduction: string;
  signatureDate: DateRange;
  dateTransmitted: DateRange;
  originalDate: DateRange;
  statuses: FilterOptionUnion[];
  sourceTypeId: FilterOptionUnion;
  isNewYork: boolean;
};

const EmptyApplicationsFilter: PolicyApplicationsFilter = {
  agentNumbers: [],
  employerName: '',
  accountNumber: '',
  writingAgent: '',
  writingAgentNumber: '',
  insuredName: '',
  insuredIdentifier: '',
  employeeName: '',
  employeeIdentifier: '',
  relation: '',
  planName: '',
  premiumMode: '',
  deductionAmount: '',
  annualDeduction: '',
  signatureDate: EmptyDateRange,
  dateTransmitted: EmptyDateRange,
  originalDate: EmptyDateRange,
  statuses: [],
  sourceTypeId: FilterOptionValue.EmptyOption(),
  isNewYork: false,
};

export const changeSort = (sort?: Sort) => createAction(PolicyApplicationsActionType.CHANGE_SORT, {sort});
export const clearSort = () => createAction(PolicyApplicationsActionType.CLEAR_SORT);

export const getApplications = (
  encryptedIdentifier: string,
  filter: PolicyApplicationsFilter,
  page?: Page<PolicyApplicationDto>,
  sort?: Sort
) => {
  return createAction(PolicyApplicationsActionType.FETCH_APPLICATIONS, {page, encryptedIdentifier, filter, sort});
};
export const changeApplicationsFilter = (filter: PolicyApplicationsFilter) =>
  createAction(PolicyApplicationsActionType.CHANGE_APPLICATIONS_FILTER, {filter});
export const changeApplications = (applications: AsyncPage<Page<PolicyApplicationDto>>) =>
  createAction(PolicyApplicationsActionType.CHANGE_APPLICATIONS, applications);

type PolicyApplicationsState = {
  applications: AsyncPage<Page<PolicyApplicationDto>>;
  filters: PolicyApplicationsFilter;
  sort: O.Option<Sort>;
};

type PolicyApplicationsAction =
  | ReturnType<typeof changeApplicationsFilter>
  | ReturnType<typeof changeApplications>
  | ReturnType<typeof clearSort>
  | ReturnType<typeof changeSort>;

const DEFAULT_STATE: PolicyApplicationsState = {
  applications: new Empty(),
  filters: EmptyApplicationsFilter,
  sort: O.none,
};

export default (
  state: PolicyApplicationsState = DEFAULT_STATE,
  action: PolicyApplicationsAction
): PolicyApplicationsState => {
  switch (action.type) {
    case PolicyApplicationsActionType.CHANGE_APPLICATIONS_FILTER:
      return {
        ...state,
        filters: action.payload.filter,
      };
    case PolicyApplicationsActionType.CHANGE_APPLICATIONS:
      return {
        ...state,
        applications: action.payload,
      };
    case PolicyApplicationsActionType.CLEAR_SORT:
      return {
        ...state,
        sort: DEFAULT_STATE.sort,
      };
    case PolicyApplicationsActionType.CHANGE_SORT:
      return {
        ...state,
        sort: O.fromNullable(action.payload.sort),
      };
    default:
      return state;
  }
};
